import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb, escurecer, clarear,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// Relatório de Indicações — Marcki v1
// ============================================================

const generatePdfRelatorioIndicacoes = async (data, filtro) => {
  if (!data) return;

  const pdfDoc   = await PDFDocument.create();
  const fontReg  = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio:  rgbHex(coresHex.cinzaMedio),
    cinzaClaro:  rgbHex(coresHex.cinzaClaro),
    laranja:     rgbHex(coresHex.laranja),
    laranjaClaro:rgbHex(coresHex.laranjaClaro),
    laranjaEsc:  rgbHex(coresHex.laranjaEscuro),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgbHex(coresHex.borda),
  };

  const brl = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const num = (v) => Number(v || 0).toLocaleString('pt-BR');

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.borda, borderWidth: 0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc || C.borda);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 7.5; const fn = c.fn || fontReg;
        let tx = c.x + 3;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 3;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // ── Logo ────────────────────────────────────────────────────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  const drawHeader = (pg) => {
    drawRect(pg, 0, H - 44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H - 44, 6, 44, C.laranja);
    if (logoImage) {
      const lDims = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x: ML, y: H - 38, width: lDims.width, height: lDims.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    const titulo = 'Relatório de Indicações';
    drawText(pg, titulo, W - MR - fontBold.widthOfTextAtSize(titulo, 9), H - 24, 9, fontBold, C.branco);
    const periodo = [
      filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
      filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
    ].filter(Boolean).join(' — ');
    if (periodo) drawText(pg, periodo, W - MR - fontReg.widthOfTextAtSize(periodo, 8), H - 36, 8, fontReg, rgb(0.8, 0.8, 0.8));
    return H - 58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `${empresa?.nome || 'Marcki Engenharia'} — ${empresa?.enderecoRodape || 'Araguari/MG'}`, ML + 8, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W - MR - 70, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
  };

  let page = pdfDoc.addPage([W, H]);
  let y = drawHeader(page);
  const novaPaginaSeNecessario = (alturaMin = 50) => {
    if (y < alturaMin) {
      drawFooter(page);
      page = pdfDoc.addPage([W, H]);
      y = drawHeader(page);
    }
  };

  const linhas = data.linhas || [];
  const r = data.resumo || {};

  // ── KPIs ──────────────────────────────────────────────────────────────────
  y -= 8;
  const kpiW = (CW - 15) / 4;
  const kpiH = 36;
  const kpis = [
    { label: 'Indicações',     valor: String(linhas.length) },
    { label: 'Total Pago',     valor: brl(r.totalPago) },
    { label: 'Total Vendido',  valor: brl(r.totalVendido) },
    { label: 'Potência (kWp)', valor: `${Number(r.potenciaVendida || 0).toFixed(2)}` },
  ];
  kpis.forEach((kpi, i) => {
    const x = ML + i * (kpiW + 5);
    drawBRect(page, x, y - kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(page, x, y - kpiH, 3, kpiH, C.laranja);
    drawText(page, kpi.label, x + 6, y - 13, 7, fontReg, C.cinzaMedio);
    drawText(page, kpi.valor, x + 6, y - 28, kpi.valor.length > 13 ? 9 : 11, fontBold, C.cinzaChumbo);
  });
  y -= kpiH + 16;

  // ── Seção 1: Relação de Pagamentos ────────────────────────────────────────
  drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
  drawText(page, '1. RELAÇÃO DE PAGAMENTOS', ML + 6, y + 7, 8, fontBold, C.branco);
  y -= 16;

  const c1 = CW * 0.15;
  const c2 = CW * 0.17;
  const c3 = CW * 0.12;
  const c4 = CW * 0.08;
  const c5 = CW * 0.12;
  const c6 = CW * 0.12;
  const c7 = CW * 0.12;
  const c8 = CW - c1 - c2 - c3 - c4 - c5 - c6 - c7;
  const headPag = (yy) => drawRow(page, [
    { x: ML,                           w: c1, fill: C.laranja, text: 'Indicador',  fn: fontBold, cl: C.branco },
    { x: ML+c1,                        w: c2, fill: C.laranja, text: 'Cliente',    fn: fontBold, cl: C.branco },
    { x: ML+c1+c2,                     w: c3, fill: C.laranja, text: 'Valor Proj.', fn: fontBold, cl: C.branco, al: 'r' },
    { x: ML+c1+c2+c3,                  w: c4, fill: C.laranja, text: 'kWp',        fn: fontBold, cl: C.branco, al: 'r' },
    { x: ML+c1+c2+c3+c4,               w: c5, fill: C.laranja, text: 'Política',   fn: fontBold, cl: C.branco },
    { x: ML+c1+c2+c3+c4+c5,            w: c6, fill: C.laranja, text: 'Valor Pago', fn: fontBold, cl: C.branco, al: 'r' },
    { x: ML+c1+c2+c3+c4+c5+c6,         w: c7, fill: C.laranja, text: 'Consultor',  fn: fontBold, cl: C.branco },
    { x: ML+c1+c2+c3+c4+c5+c6+c7,      w: c8, fill: C.laranja, text: 'Fabricante', fn: fontBold, cl: C.branco },
  ], yy);

  y = headPag(y);
  if (linhas.length === 0) {
    drawText(page, 'Nenhuma indicação no período selecionado.', ML + 4, y - 16, 8, fontReg, C.cinzaMedio);
    y -= 24;
  } else {
    linhas.forEach((l, i) => {
      novaPaginaSeNecessario(50);
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      y = drawRow(page, [
        { x: ML,                      w: c1, fill, text: (l.indicador || '').substring(0, 16) },
        { x: ML+c1,                   w: c2, fill, text: (l.cliente || '').substring(0, 18) },
        { x: ML+c1+c2,                w: c3, fill, text: brl(l.valor), al: 'r' },
        { x: ML+c1+c2+c3,             w: c4, fill, text: Number(l.potencia || 0).toFixed(2), al: 'r' },
        { x: ML+c1+c2+c3+c4,          w: c5, fill, text: (l.politica || '').substring(0, 14) },
        { x: ML+c1+c2+c3+c4+c5,       w: c6, fill, text: brl(l.valorPago), al: 'r' },
        { x: ML+c1+c2+c3+c4+c5+c6,    w: c7, fill, text: (l.consultor || '').substring(0, 14) },
        { x: ML+c1+c2+c3+c4+c5+c6+c7, w: c8, fill, text: (l.fabricante || '').substring(0, 14) },
      ], y);
    });
    // Total pago
    drawBRect(page, ML, y - tH + 2, CW, tH, C.laranjaClaro, C.laranja);
    drawText(page, `Total de indicações: ${linhas.length}`, ML + 4, y - tH + 5, 8, fontBold, C.laranjaEsc);
    const tp = `Total Pago: ${brl(r.totalPago)}`;
    drawText(page, tp, ML + CW - fontBold.widthOfTextAtSize(tp, 8) - 3, y - tH + 5, 8, fontBold, C.laranjaEsc);
    y -= tH + 18;
  }

  // ── Seção 2: Resumo (métricas) ────────────────────────────────────────────
  novaPaginaSeNecessario(180);
  drawRect(page, ML, y + 2, CW, 16, C.cinzaChumbo);
  drawText(page, '2. RESUMO', ML + 6, y + 7, 8, fontBold, C.branco);
  y -= 16;

  const metricas = [
    ['Total pago', brl(r.totalPago)],
    ['Total vendido', brl(r.totalVendido)],
    ['Participação no custo', `${Number(r.participacaoCusto || 0).toFixed(2)}%`],
    ['Potência vendida', `${Number(r.potenciaVendida || 0).toFixed(2)} kWp`],
    ['Política mais usada', r.politicaMaisUsada || '—'],
    ['Maior indicador', r.maiorIndicador || '—'],
    ['Consultor com mais indicações', r.consultorComMaisIndicacoes || '—'],
    ['Fabricante mais utilizado', r.fabricanteMaisUtilizado || '—'],
    ['Ticket Médio (R$)', brl(r.ticketMedioReais)],
    ['Ticket Médio (kWp)', Number(r.ticketMedioKwp || 0).toFixed(2)],
    ['Ticket Médio Indicações (R$)', brl(r.ticketMedioIndicacao)],
    ['Taxa de Conversão', `${Number(r.taxaConversao || 0).toFixed(2)}%`],
  ];
  const m1 = CW * 0.55;
  const m2 = CW - m1;
  metricas.forEach(([k, v], i) => {
    novaPaginaSeNecessario(50);
    const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
    y = drawRow(page, [
      { x: ML,    w: m1, fill, text: k, fn: fontBold, sz: 8 },
      { x: ML+m1, w: m2, fill, text: v, al: 'r', sz: 8 },
    ], y);
  });

  drawFooter(page);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type: 'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = 'relatorio-indicacoes.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRelatorioIndicacoes;